/**
 * The whole app, five screens deep:
 *
 *   Doors    — you are outside. Pick a name, walk in, or read the records.
 *   Bar      — you are in a saloon. Who else is here, ante up, start.
 *   Range    — fifteen seconds of practice, before anything is at stake.
 *   Hunt     — the canvas.
 *   Results  — the standings, the records, and the door back to the bar.
 *
 * React owns all of them EXCEPT the inside of the hunt, which is a canvas that
 * reads `world` at frame rate. That split is the performance design: React
 * re-renders per human action, the canvas re-renders per frame, and they never
 * fight over the same data.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import {
  connect,
  getSnapshot,
  subscribe,
  refreshSaloons,
} from './net/connection.js';
import { AimController } from './hunt/aim.js';
import { audio } from './hunt/audio.js';
import { Range } from './hunt/range.js';
import { world } from './hunt/world.js';
import { fire as fireShot } from './net/connection.js';
import { Doors } from './saloon/Doors.js';
import { Bar } from './saloon/Bar.js';
import { Results } from './saloon/Results.js';
import { HuntScreen } from './hunt/HuntScreen.js';
import { SettingsSheet } from './ui/SettingsSheet.js';
import { loadSettings, modeWord, prefersReducedMotion, saveSettings, type Settings } from './ui/settings.js';

export function App() {
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const [settings, setSettingsState] = useState<Settings>(loadSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [range, setRange] = useState<Range | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  /**
   * One aim controller for the life of the app. It holds the gyro subscription
   * and the calibration reference, and re-creating it between the range and the
   * hunt would drop both — the player would have to re-permit and re-centre
   * between practice and their first wave.
   */
  const aim = useMemo(() => new AimController(() => fireRef.current()), []);
  const fireRef = useRef<() => void>(() => {});
  const rangeRef = useRef<Range | null>(null);
  rangeRef.current = range;

  fireRef.current = useCallback(() => {
    const a = aim.state;
    const r = rangeRef.current;
    if (r && !r.state.done) {
      r.fire(a.sx, a.sy);
      return;
    }
    if (!world.active) return;
    fireShot(a.sx, a.sy);
  }, [aim]);

  // ── boot ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    connect();
    world.reducedMotion = prefersReducedMotion();
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const onMq = () => {
      world.reducedMotion = mq.matches;
    };
    mq.addEventListener?.('change', onMq);
    return () => mq.removeEventListener?.('change', onMq);
  }, []);

  useEffect(() => {
    aim.setOptions({ sensitivity: settings.sensitivity, invertY: settings.invertY });
    audio.setVolume(settings.volume);
    audio.setMuted(!settings.sound);
    audio.setMusic(settings.music);
  }, [aim, settings]);

  /**
   * Unlock the AudioContext on the first interaction of any kind.
   *
   * "Take the gun" is still where the motion permission is asked for, because
   * that has to be a deliberate act. Audio does not: a desktop player who
   * arrives by invite link, clicks Join and never touches that button would
   * otherwise play a silent game and conclude the sound is broken. One capture
   * listener, removed the moment it fires.
   */
  useEffect(() => {
    const once = () => void audio.unlock();
    const opts = { once: true, capture: true } as const;
    window.addEventListener('pointerdown', once, opts);
    window.addEventListener('keydown', once, opts);
    return () => {
      window.removeEventListener('pointerdown', once, true);
      window.removeEventListener('keydown', once, true);
    };
  }, []);

  const applySettings = useCallback(
    (next: Settings) => {
      setSettingsState(next);
      saveSettings(next);
      if (next.mode !== aim.state.mode) {
        if (next.mode === 'gyro') void aim.requestGyro();
        else aim.setMode('touch');
      }
    },
    [aim],
  );

  /**
   * Errors surface as a toast rather than a dialog: a modal in the middle of a
   * wave would be a bigger problem than whatever it was reporting.
   *
   * EVERY toast goes through this one function, and it always sets the dismiss
   * timer. The first version had the timer on the `snap.error` effect only, so
   * a toast raised directly — "this device has no motion sensor", the one
   * almost every desktop player sees — never went away. It was still sitting
   * over the wave counter nine waves later, which the screenshot pass caught.
   */
  const toastTimer = useRef<number | undefined>(undefined);
  const showToast = useCallback((text: string) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (snap.error) showToast(snap.error);
  }, [snap.error, showToast]);

  // The bar has music; the woods do not.
  const inHunt = snap.view?.phase === 'wave' || snap.view?.phase === 'intermission' || snap.view?.phase === 'countdown';
  useEffect(() => {
    if (!settings.music || !audio.ready) return;
    if (inHunt || range) audio.stopMusic();
    else audio.startMusic();
  }, [inHunt, range, settings.music, snap.status]);

  /**
   * The woods are not silent, though. `waveNonce` is in the dependency list
   * because that is what changes when the site does — the bed has to follow
   * dawn → midday → dusk → overcast → night across a hunt, and a wave message
   * is the only thing that tells us which one we are in.
   */
  useEffect(() => {
    if (!audio.ready) return;
    if (inHunt || range) audio.startAmbience(world.site.ambience);
    else audio.stopAmbience();
  }, [inHunt, range, snap.waveNonce, snap.status, settings.sound]);

  useEffect(() => () => audio.stopAmbience(), []);

  /**
   * The one gesture that unlocks everything a browser gates behind a user
   * gesture: the AudioContext, and iOS's motion permission. Wired to "take the
   * gun", which every player presses before they can play.
   */
  const takeTheGun = useCallback(async () => {
    await audio.unlock();
    audio.play('coin');
    if (settings.mode === 'gyro') {
      const status = await aim.requestGyro();
      if (status === 'denied' || status === 'unavailable') {
        // `aim` has already landed on the right conventional input for this
        // machine — mouse where there is one, thumb otherwise. Read it back
        // rather than deciding again here, so there is one rule and not two.
        const fell = aim.state.mode;
        showToast(
          status === 'denied'
            ? `No motion access — switched to ${modeWord(fell)}. Both work the same.`
            : `No motion sensor on this device — using ${modeWord(fell)}.`,
        );
        applySettings({ ...settings, mode: fell });
      }
    } else {
      aim.setMode(settings.mode);
    }
  }, [aim, applySettings, settings, showToast]);

  const openRange = useCallback(async () => {
    await takeTheGun();
    const r = new Range();
    r.start();
    setRange(r);
  }, [takeTheGun]);

  const closeRange = useCallback(() => {
    rangeRef.current?.stop();
    setRange(null);
    applySettings({ ...settings, trained: true });
  }, [applySettings, settings]);

  // ── screens ───────────────────────────────────────────────────────────────

  const settingsSheet = showSettings ? (
    <SettingsSheet
      settings={settings}
      aim={aim}
      onChange={applySettings}
      onClose={() => setShowSettings(false)}
      onRange={() => {
        setShowSettings(false);
        void openRange();
      }}
    />
  ) : null;

  const toastEl = toast ? (
    <div className="toast" role="status" aria-live="polite">
      {toast}
    </div>
  ) : null;

  if (range) {
    return (
      <>
        <HuntScreen
          aim={aim}
          settings={settings}
          range={range}
          onOpenSettings={() => setShowSettings(true)}
          onLeaveRange={closeRange}
        />
        {settingsSheet}
        {toastEl}
      </>
    );
  }

  const view = snap.view;
  let screen: JSX.Element;

  if (!view) {
    screen = (
      <Doors
        snap={snap}
        onRefresh={refreshSaloons}
        onOpenSettings={() => setShowSettings(true)}
        onRange={openRange}
        onTouchAudio={() => void audio.unlock()}
      />
    );
  } else if (view.phase === 'over') {
    screen = <Results snap={snap} onOpenSettings={() => setShowSettings(true)} />;
  } else if (view.phase === 'bar') {
    screen = (
      <Bar
        snap={snap}
        settings={settings}
        onTakeGun={takeTheGun}
        onOpenSettings={() => setShowSettings(true)}
        onRange={openRange}
      />
    );
  } else {
    screen = (
      <HuntScreen
        aim={aim}
        settings={settings}
        range={null}
        onOpenSettings={() => setShowSettings(true)}
        onLeaveRange={null}
      />
    );
  }

  return (
    <>
      {screen}
      {settingsSheet}
      {toastEl}
    </>
  );
}
